import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { timeEntriesApi, employeesApi, projectsApi } from '../api';
import { dollarsToCents, centsToDollarString, centsToDollarInput, fmtDate } from '../constants';
import { MoneyInput } from './MoneyInput';
import { payBasisLabel } from '../peopleConstants';
import { TIME_STATUS_STYLES, timeStatusLabel, fmtMinutes, payDisplay, costDisplay } from '../laborConstants';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls = 'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

const Row = ({ l, v }) => (
  <div className="flex justify-between gap-3 py-1 text-sm">
    <span className="text-gray-500">{l}</span>
    <span className="text-gray-200 text-right">{v}</span>
  </div>
);

export const TimeEntryFormDialog = ({ open, onOpenChange, entry, lockedProjectId, lockedEmployeeId, onSaved }) => {
  const isNew = !entry;
  const status = entry?.status;
  const editable = isNew || status === 'draft';
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ employee_id: '', project_id: '', work_date: '', hours: '', minutes: '', description: '', work_category: '', manual_pay: '' });
  const lockedP = lockedProjectId !== undefined && lockedProjectId !== null;

  useEffect(() => {
    if (!open) return;
    employeesApi.list({ archived: false, employment_status: 'active' }).then((r) => setEmployees(r.data)).catch(() => {});
    projectsApi.list({ archived: false, sort: 'name_az' }).then((r) => setProjects(r.data)).catch(() => {});
    if (entry) {
      setF({
        employee_id: entry.employee_id, project_id: entry.project_id || '',
        work_date: entry.work_date, hours: String(Math.floor(entry.duration_minutes / 60)),
        minutes: String(entry.duration_minutes % 60), description: entry.description || '',
        work_category: entry.work_category || '', manual_pay: centsToDollarInput(entry.manual_pay_amount_cents),
      });
    } else {
      setF({ employee_id: lockedEmployeeId || '', project_id: lockedP ? lockedProjectId : '', work_date: new Date().toISOString().slice(0, 10), hours: '', minutes: '', description: '', work_category: '', manual_pay: '' });
    }
  }, [open, entry, lockedP, lockedProjectId, lockedEmployeeId]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const emp = isNew || editable ? employees.find((e) => e.id === f.employee_id) : null;
  const snapBasis = editable && emp ? emp.pay_basis : entry?.pay_basis_snapshot;
  const snapRate = editable && emp ? emp.default_pay_rate_cents : entry?.pay_rate_snapshot_cents;
  const snapCost = editable && emp ? emp.internal_hourly_cost_rate_cents : entry?.internal_hourly_cost_rate_snapshot_cents;
  const dur = (parseInt(f.hours || '0', 10) || 0) * 60 + (parseInt(f.minutes || '0', 10) || 0);
  const hourly = snapBasis === 'hourly';
  const previewPay = hourly && snapRate != null ? Math.round((dur * snapRate) / 60) : dollarsToCents(f.manual_pay);
  const previewCost = snapCost != null ? Math.round((dur * snapCost) / 60) : null;

  const payload = () => ({
    employee_id: f.employee_id, project_id: f.project_id || null,
    work_date: f.work_date, duration_minutes: dur, description: f.description || null,
    work_category: f.work_category || null,
    manual_pay_amount_cents: hourly ? null : dollarsToCents(f.manual_pay),
  });

  const validate = () => {
    if (!f.employee_id) { toast.error('Select an employee'); return false; }
    if (!f.work_date) { toast.error('Work date is required'); return false; }
    if (dur <= 0 || dur > 1440) { toast.error('Duration must be between 1 minute and 24 hours'); return false; }
    return true;
  };

  const save = async (approve = false) => {
    if (busy || !validate()) return;
    setBusy(true);
    try {
      const res = isNew ? await timeEntriesApi.create(payload()) : await timeEntriesApi.update(entry.id, payload());
      let doc = res.data;
      if (approve) doc = (await timeEntriesApi.approve(doc.id)).data;
      toast.success(approve ? 'Time entry approved' : 'Time entry saved as draft');
      onOpenChange(false);
      onSaved?.(doc);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save time entry');
    } finally { setBusy(false); }
  };

  const action = async (fn, msg) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Action failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="time-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isNew ? 'Add Time Entry' : 'Time Entry'}
            {status && <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${TIME_STATUS_STYLES[status]}`}>{timeStatusLabel(status)}</span>}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {editable ? 'Draft entries do not affect financial totals until approved.' : status === 'approved' ? 'Approved — reopen to draft to edit.' : 'Voided — restore to draft to use again.'}
          </DialogDescription>
        </DialogHeader>

        {editable ? (
          <div className="space-y-4 py-1">
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Employee *</label>
              <select value={f.employee_id} onChange={(e) => set('employee_id', e.target.value)} className={selectCls} disabled={Boolean(lockedEmployeeId)} data-testid="te-employee">
                <option value="">Select employee…</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.display_name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Date *</label>
                <Input type="date" value={f.work_date} onChange={(e) => set('work_date', e.target.value)} className={inputCls} data-testid="te-date" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Hours</label>
                <Input type="number" min="0" max="24" value={f.hours} onChange={(e) => set('hours', e.target.value)} className={inputCls} data-testid="te-hours" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Minutes</label>
                <Input type="number" min="0" max="59" value={f.minutes} onChange={(e) => set('minutes', e.target.value)} className={inputCls} data-testid="te-minutes" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Project</label>
              <select value={f.project_id} onChange={(e) => set('project_id', e.target.value)} className={selectCls} disabled={lockedP} data-testid="te-project">
                <option value="">General BuildBroz Time</option>
                {lockedP && !projects.some((p) => p.id === lockedProjectId) && <option value={lockedProjectId}>Current Project</option>}
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Description</label>
                <Input value={f.description} onChange={(e) => set('description', e.target.value)} className={inputCls} data-testid="te-description" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Work Category</label>
                <Input value={f.work_category} onChange={(e) => set('work_category', e.target.value)} placeholder="e.g., Flooring" className={inputCls} data-testid="te-category" />
              </div>
            </div>

            {f.employee_id && (
              <div className="bg-[#1A1A1A] border border-gray-800 rounded-md p-3" data-testid="te-pay-preview">
                <Row l="Pay Basis" v={snapBasis ? payBasisLabel(snapBasis) : 'Not set'} />
                <Row l="Default Pay Rate" v={snapRate != null ? centsToDollarString(snapRate) : 'Not set'} />
                <Row l="Internal Hourly Cost Rate" v={snapCost != null ? centsToDollarString(snapCost) : 'Not set'} />
                {hourly ? (
                  <>
                    <Row l="Estimated Employee Pay" v={previewPay != null && dur > 0 ? centsToDollarString(previewPay) : 'Not calculated'} />
                    <Row l="Recorded Labor Cost" v={previewCost != null && dur > 0 ? centsToDollarString(previewCost) : 'Rate not set'} />
                  </>
                ) : (
                  <div className="mt-2">
                    <p className="text-xs text-amber-400/90 mb-2">
                      Pay is not automatically calculated for this employee's pay basis. Enter an amount only when this work entry should contribute to pay owed.
                    </p>
                    <MoneyInput value={f.manual_pay} onChange={(v) => set('manual_pay', v)} placeholder="Manual pay amount ($, optional)" className={inputCls} data-testid="te-manual-pay" />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1 py-1" data-testid="te-readonly">
            <Row l="Employee" v={entry.employee?.display_name} />
            <Row l="Date" v={fmtDate(entry.work_date)} />
            <Row l="Duration" v={fmtMinutes(entry.duration_minutes)} />
            <Row l="Project" v={entry.project ? entry.project.name : 'General BuildBroz'} />
            {entry.description && <Row l="Description" v={entry.description} />}
            {entry.work_category && <Row l="Category" v={entry.work_category} />}
            <div className="border-t border-gray-800 my-2" />
            <Row l="Pay Basis Snapshot" v={entry.pay_basis_snapshot ? payBasisLabel(entry.pay_basis_snapshot) : 'Not set'} />
            <Row l="Pay Rate Snapshot" v={entry.pay_rate_snapshot_cents != null ? centsToDollarString(entry.pay_rate_snapshot_cents) : 'Not set'} />
            <Row l="Cost Rate Snapshot" v={entry.internal_hourly_cost_rate_snapshot_cents != null ? centsToDollarString(entry.internal_hourly_cost_rate_snapshot_cents) : 'Not set'} />
            <Row l="Employee Pay" v={payDisplay(entry.pay_amount_cents)} />
            <Row l="Labor Cost" v={costDisplay(entry.labor_cost_amount_cents)} />
            <div className="border-t border-gray-800 my-2" />
            {entry.approved_at && <Row l="Approved" v={`${fmtDate(entry.approved_at.slice(0, 10))} by ${entry.approved_by_username}`} />}
            {entry.voided_at && <Row l="Voided" v={`${fmtDate(entry.voided_at.slice(0, 10))} by ${entry.voided_by_username}`} />}
            {entry.void_reason && <Row l="Void Reason" v={entry.void_reason} />}
            <Row l="Created" v={`${fmtDate(entry.created_at?.slice(0, 10))} by ${entry.created_by_username}`} />
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-gray-800">
          {editable && (
            <>
              {!isNew && (
                <Button variant="outline" onClick={() => action(() => timeEntriesApi.void(entry.id), 'Time entry voided')} disabled={busy} className="border-red-900 text-red-400 mr-auto" data-testid="te-void">Void</Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-700 text-gray-300">Cancel</Button>
              <Button variant="outline" onClick={() => save(false)} disabled={busy} className="border-gray-700 text-gray-300" data-testid="te-save-draft">
                {busy && <Loader2 size={14} className="animate-spin" />} Save Draft
              </Button>
              <Button onClick={() => save(true)} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="te-save-approve">
                {busy && <Loader2 size={14} className="animate-spin" />} Save &amp; Approve
              </Button>
            </>
          )}
          {status === 'approved' && (
            <>
              <Button variant="outline" onClick={() => action(() => timeEntriesApi.void(entry.id), 'Time entry voided')} disabled={busy} className="border-red-900 text-red-400 mr-auto" data-testid="te-void">Void</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300">Close</Button>
              <Button onClick={() => action(() => timeEntriesApi.reopen(entry.id), 'Reopened to draft')} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="te-reopen">Reopen to Draft</Button>
            </>
          )}
          {status === 'voided' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300">Close</Button>
              <Button onClick={() => action(() => timeEntriesApi.restore(entry.id), 'Restored to draft')} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="te-restore">Restore to Draft</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
